import React, { useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useReactiveVar, useQuery } from '@apollo/client';
import FingoTable from '../../../../components/tables/FingoTable';
import FingoMainView from '../../../../components/layout/FingoMainView';
import InformationStack from '../../../../components/stacks/InformationStack';
import { shoppingCartOffersVar } from '../../../../apollo/reactive-variables/shopping-cart';
import {
  getDebtorAveragePaymentDate,
  getValidatedDateToPay,
} from '../../../../helpers/date-to-pay-restrictions';
import { areDatesEqual } from '../../../../constants/dates';
import useIsMobile from '../../../../hooks/useIsMobile';
import { ONBOARDING_TABLE_CLASS_NAME } from '../../../../theme/constants/table';
import { DATE_WARNING_TYPES } from '../../../../constants/date-warning-types';
import useCustomWarnings from '../../../../hooks/useCustomWarnings';
import SelectDateForAllSwitch from '../../../../components/switches/SelectDateForAllSwitch';
import DateWarning from '../../../../components/warnings/DateWarning';
import useGetCountryFromUrl from '../../../../hooks/useGetCountryFromUrl';
import getSelectedInvoicesDateInformation from '../../../../hooks/getSelectedInvoicesDateInformation';
import { INFORMATION_STACK_CLASS_NAME } from '../../../../theme/constants/stack';
import { DEFAULT_ICON_CLASS_NAME } from '../../../../theme/constants/icon';
import GET_USER from '../../../../graphql/queries/get-user';
import AvailableOffersSummaryDetails from './AvailableOffersSummaryDetails';
import { useUpcomingHolidays } from '../../../../hooks/useUpcomingHolidays';

const AvailableOffersSummary = ({
  selectedClients,
  queryDocument,
  summaryHeaders,
  showInformationStack,
  useSelectDaysButtons,
  minDaysFromToday,
  informationStackText,
  getClientColumns,
  getDetailsColumns,
  detailsHeaders,
  detailsMobileHeaders,
}) => {
  const isMobile = useIsMobile();
  const shoppingCartOffers = useReactiveVar(shoppingCartOffersVar);
  const [initialExpandedRow, setInitialExpandedRow] = useState(null);
  const [sameDateForAllSelection, setSameDateForAllSelection] = useState(null);
  const country = useGetCountryFromUrl();
  const { holidayDates } = useUpcomingHolidays();
  const { data: userData } = useQuery(GET_USER);
  const maxOperationDays = userData?.getUser?.selectedCompany
    ?.activeCompanyConditionsContract?.conditions?.maxOperationDays;

  const shouldKeepWarning = useCallback((warning) => {
    // For CONTRACT_MAX_DAYS_EXCEEDED, keep the warning if dateToPay is still null
    // since this warning indicates the date selection was rejected
    if (warning?.type === DATE_WARNING_TYPES.CONTRACT_MAX_DAYS_EXCEEDED) {
      const currentShoppingCartOffers = shoppingCartOffersVar();
      const specificInvoice = currentShoppingCartOffers.find(
        (offer) => offer.invoiceId === warning?.metadata?.invoiceId,
      );
      const currentDateToPay = specificInvoice?.dateToPay;
      // Keep warning if dateToPay is still null (date was rejected)
      return !currentDateToPay;
    }

    const currentShoppingCartOffers = shoppingCartOffersVar();
    const specificInvoice = currentShoppingCartOffers.find(
      (offer) => offer.invoiceId === warning?.metadata?.invoiceId,
    );
    const currentDateToPay = specificInvoice?.dateToPay;
    const warningDateToPay = warning.metadata?.dateToPay;
    return areDatesEqual(currentDateToPay, warningDateToPay);
  }, [shoppingCartOffers]);

  const {
    executeWithWarningHandling,
    throwWarning,
    getFirstWarningByType,
  } = useCustomWarnings(shouldKeepWarning);

  const offersDateInformation = getSelectedInvoicesDateInformation(shoppingCartOffers);

  const offersDateInformationMap = useMemo(
    () => offersDateInformation?.invoices?.edges?.reduce((acc, edge) => {
      acc[edge.node.id] = {
        id: edge.node.id,
        dateIssued: edge.node.dateIssued,
        dateExpiration: edge.node.dateExpiration,
        offer: {
          fundingDaysAfterExpiration: edge.node.offer.fundingDaysAfterExpiration,
        },
      };
      return acc;
    }, {}),
    [offersDateInformation],
  );

  const updateDateToPay = useCallback((offer, dateToPay) => {
    const currentOffers = shoppingCartOffersVar();
    const updatedOffer = { ...offer, dateToPay };
    shoppingCartOffersVar([
      updatedOffer,
      ...currentOffers.filter((o) => o.invoiceId !== offer.invoiceId),
    ]);
  }, []);

  const validateAndUpdateDateToPay = useCallback((offer, newValue, validationMethod) => {
    const dateToPay = validationMethod(
      newValue,
      offersDateInformationMap?.[offer.invoiceId],
      country,
      throwWarning,
      maxOperationDays,
      holidayDates,
    );
    updateDateToPay(offer, dateToPay);
  }, [
    offersDateInformationMap,
    country,
    throwWarning,
    maxOperationDays,
    holidayDates,
    updateDateToPay,
  ]);

  const selectedDaysButtonsOnChange = useCallback((newValue, row) => {
    shoppingCartOffers.forEach((offer) => {
      if (row.clientOffers.includes(offer.invoiceId)) {
        executeWithWarningHandling(
          () => validateAndUpdateDateToPay(offer, newValue, getValidatedDateToPay),
          (dateToPay) => updateDateToPay(offer, dateToPay),
        );
      }
    });
  }, [shoppingCartOffers, executeWithWarningHandling, validateAndUpdateDateToPay, updateDateToPay]);

  const selectedDaysButtonsOnRecommendedPaymentTerm = useCallback((_, row) => {
    shoppingCartOffers.forEach((offer) => {
      if (row.clientOffers.includes(offer.invoiceId)) {
        executeWithWarningHandling(
          () => validateAndUpdateDateToPay(
            offer,
            row?.invoicesActualPaymentTerm,
            getDebtorAveragePaymentDate,
          ),
          (dateToPay) => updateDateToPay(offer, dateToPay),
        );
      }
    });
  }, [shoppingCartOffers, executeWithWarningHandling, validateAndUpdateDateToPay, updateDateToPay]);

  const setAllDates = useCallback((newValue) => {
    shoppingCartOffers.forEach((offer) => {
      executeWithWarningHandling(
        () => validateAndUpdateDateToPay(offer, newValue, getValidatedDateToPay),
        (dateToPay) => updateDateToPay(offer, dateToPay),
      );
    });
  }, [shoppingCartOffers, executeWithWarningHandling, validateAndUpdateDateToPay, updateDateToPay]);

  const columns = getClientColumns(
    selectedDaysButtonsOnChange,
    selectedDaysButtonsOnRecommendedPaymentTerm,
    isMobile,
    sameDateForAllSelection,
    minDaysFromToday,
  );

  const CollapseDetails = useCallback((props) => (
    <AvailableOffersSummaryDetails
      {...props}
      getColumns={getDetailsColumns}
      detailsHeaders={detailsHeaders}
      detailsMobileHeaders={detailsMobileHeaders}
    />
  ), [getDetailsColumns, detailsHeaders, detailsMobileHeaders]);

  const weekendWarning = getFirstWarningByType(DATE_WARNING_TYPES.WEEKEND);
  const maxDateExceededWarning = getFirstWarningByType(DATE_WARNING_TYPES.MAX_DATE_EXCEEDED);
  const contractMaxDaysExceededWarning = getFirstWarningByType(
    DATE_WARNING_TYPES.CONTRACT_MAX_DAYS_EXCEEDED,
  );

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        height="fit-content"
        sx={{ mb: 2, mr: 2, gap: 2 }}
        flexDirection={isMobile ? 'column' : 'row'}
      >
        {showInformationStack ? (
          <InformationStack
            text={informationStackText}
            className={INFORMATION_STACK_CLASS_NAME}
            variant="small_2"
            IconComponent={<InfoOutlinedIcon className={DEFAULT_ICON_CLASS_NAME} />}
          />
        ) : (
          <Box />
        )}
        <SelectDateForAllSwitch
          setAllDates={setAllDates}
          setSameDateForAllSelection={setSameDateForAllSelection}
          useSelectDaysButtons={useSelectDaysButtons}
          minDaysFromToday={minDaysFromToday}
        />
      </Box>
      {contractMaxDaysExceededWarning && (
        <DateWarning
          warning={contractMaxDaysExceededWarning}
          sx={{ mb: 2 }}
        />
      )}
      {weekendWarning && (
        <DateWarning
          warning={weekendWarning}
          sx={{ mb: 2 }}
        />
      )}
      {maxDateExceededWarning && (
        <DateWarning
          warning={maxDateExceededWarning}
          sx={{ mb: 2 }}
        />
      )}

      <FingoMainView
        id="available-offers"
        query={queryDocument}
        hideReloadButton
        queryCustomVariables={{
          id_In: selectedClients,
        }}
        queryFetchPolicy="cache-first"
        onCompletedSetRows={(data) => {
          const rows = Object.values(data)[0].edges.map((edge) => edge.node);
          if (rows.length > 0) {
            // Se usa el timeout para asegurar que la actualización de estado se ejecute una vez
            // que el componente se haya renderizado. Sino levanta un warning de consola.
            setTimeout(() => {
              setInitialExpandedRow(rows[0].id);
            }, 0);
          }
          return rows;
        }}
        slots={{
          table: FingoTable,
        }}
        slotProps={{
          table: {
            includeHeaders: summaryHeaders,
            showHeaders: false,
            columns,
            checkboxSelection: false,
            isRowSelectable: () => true,
            collapsible: true,
            isRowCollapsible: () => true,
            collapseComponent: CollapseDetails,
            noRowsMessage: () => <Typography>Sin ofertas</Typography>,
            rowsPerPageOptions: [10],
            disableSelectionOnClick: true,
            rowHeight: 65,
            density: 'standard',
            hideFooter: true,
            useMobileLayout: true,
            initialExpandedRow,
            tableClassName: ONBOARDING_TABLE_CLASS_NAME,
            outsideBoxProps: {
              sx: {
                height: '100%',
              },
            },
          },
        }}
      />
    </>
  );
};

AvailableOffersSummary.propTypes = {
  selectedClients: PropTypes.arrayOf(PropTypes.string).isRequired,
  queryDocument: PropTypes.shape().isRequired,
  summaryHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  showInformationStack: PropTypes.bool,
  useSelectDaysButtons: PropTypes.bool,
  minDaysFromToday: PropTypes.number,
  informationStackText: PropTypes.string.isRequired,
  getClientColumns: PropTypes.func.isRequired,
  getDetailsColumns: PropTypes.func.isRequired,
  detailsHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  detailsMobileHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
};

AvailableOffersSummary.defaultProps = {
  showInformationStack: true,
  useSelectDaysButtons: true,
  minDaysFromToday: 15,
};

export default AvailableOffersSummary;
